/**
 * @fileoverview Hook Data Layer optimizado para AreaChatWindowMC.
 * Encapsula la obtención, transformación y memoización de mensajes de conversación.
 * Cumple arquitectura Mercately: separación de capas, memoización y performance.
 * @module useAreaChatWindowData
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 2.1.0 - Optimización de renderizado y documentación JSDoc
 */

/**
 * @typedef {Object} AreaChatWindowMessage
 * @description Objeto de mensaje retornado por el hook Data Layer para AreaChatWindowMC.
 *
 * ---
 * CAMPOS PUROS DEL BACKEND
 * ---
 * @property {string|number} id - Identificador único del mensaje
 * @property {string} account_uid - UID de la cuenta asociada
 * @property {Object} assignment_data - Datos de asignación del mensaje (TODO: definir estructura)
 * @property {string} content_type - Tipo de contenido (text, media, etc.)
 * @property {boolean} note - Si es nota interna
 * @property {boolean} order_note - Si es nota de orden
 * @property {string} content_media_url - URL del contenido multimedia
 * @property {string} content_text - Texto puro del mensaje
 * @property {string} content_media_type - Subtipo de contenido multimedia
 * @property {string} direction - Dirección del mensaje (inbound/outbound)
 * @property {string} sender_full_name - Nombre completo del remitente
 * @property {string} inbound_sender_full_name - Nombre completo del remitente inbound
 * @property {boolean} from_group - Si el mensaje proviene de un grupo
 * @property {string|number} created_time - Timestamp de creación
 * @property {string|number} customer_id - ID del cliente asociado
 * @property {string} status - Estado del mensaje (enviado, error, etc.)
 * @property {string} filename - Nombre del archivo adjunto
 * @property {number} content_location_latitude - Latitud de ubicación (si aplica)
 * @property {number} content_location_longitude - Longitud de ubicación (si aplica)
 * @property {string} content_media_caption - Caption del contenido multimedia
 * @property {Object} order_summary - Resumen de orden (ver estructura abajo)
 * @property {string} error_message - Mensaje de error (si aplica)
 * @property {Object} contacts_information - Información de contactos (si aplica)
 * @property {Array} options_list - Lista de opciones (quick replies, listas)
 * @property {Object} content_ws_order - Datos de orden WS (si aplica)
 * @property {Object} content_ws_product - Datos de producto WS (si aplica)
 * @property {Object} event_data - Datos de evento
 * @property {string} mia_flag - Flag MIA
 * @property {boolean} mia_assignment - Si es asignación MIA
 * @property {boolean} mia_client_data - Si es dato de cliente MIA
 * @property {boolean} mia_event - Si es evento MIA
 * @property {boolean} sent_by_mia - Si fue enviado por MIA
 * @property {boolean} mid - token para descargar recurso de facebook (si aplica)
 * @property {Object} identifier - Identificador único del mensaje en el sistema
 * @property {Object} mia_sources_response - Fuentes asociadas al mensaje MIA
 * @property {string} reaction - Reacción al mensaje (si aplica)
 * @property {boolean} can_reply - Si se puede responder el mensaje
 * @property {string} card_header_type - Tipo de header para mensajes carousel (si aplica)
 * @property {Array} carousel_cards - Tarjetas de carousel (si aplica)
 * @property {Object} content_call_permission_reply - Datos de respuesta de permiso de llamada (si aplica)
 *
 * ---
 * CAMPOS ENRIQUECIDOS POR EL FRONTEND
 * ---
 * @property {string} componentType - Tipo de componente para renderizado
 * @property {string} messageText - Texto del mensaje formateado y con URLs detectadas
 * @property {string|null} fbUrl - URL de Facebook extraída (si aplica)
 * @property {string|null} igUrl - URL de Instagram extraída (si aplica)
 * @property {string} contentType - Alias de content_type
 * @property {string} contentMediaType - Alias de content_media_type
 * @property {string} contentMediaUrl - Alias de content_media_url
 * @property {boolean} isFromGroup - Alias de from_group
 * @property {string} contentMediaCaption - Alias de content_media_caption
 * @property {boolean} isNote - Si es nota interna (enriquecido)
 * @property {boolean} isOrderNote - Si es nota de orden (enriquecido)
 * @property {boolean} showError - Si debe mostrar error
 * @property {Object} contactsInformation - Alias de contacts_information
 * @property {Array} optionsList - Alias de options_list
 * @property {Object} eventData - Alias de event_data
 * @property {boolean} isBuyIntention - Si es intención de compra
 * @property {boolean} isGeneratedOrder - Si es orden generada
 * @property {boolean} isHumanHelp - Si es mensaje de ayuda humana
 * @property {boolean} isOrderError - Si MIA intentó generar una orden y falló
 * @property {boolean} isPaymentLink - Si MIA envió un link de pago
 * @property {boolean} isMiaAssignment - Si es asignación MIA (enriquecido)
 * @property {boolean} isMiaClientData - Si es dato de cliente MIA (enriquecido)
 * @property {boolean} isMiaEvent - Si es evento MIA (enriquecido)
 * @property {boolean} isSentByMia - Si fue enviado por MIA (enriquecido)
 * @property {boolean} isShowIcon - Si debe mostrar icono de estado
 * @property {string|number} mid - ID de mensaje externo
 * @property {Object|null} repliedMessage - Mensaje respondido enriquecido
 * @property {string} identifier - Identificador único del mensaje en el sistema
 * @property {Object|null} miaSourcesResponse - Fuentes asociadas al mensaje MIA (enriquecido)
 * @property {string|null} reaction - Reacción al mensaje (enriquecido)
 * @property {boolean} canReply - Si se puede responder el mensaje (enriquecido)
 * @property {string|null} cardHeaderType - Tipo de header para mensajes carousel (enriquecido)
 * @property {Array|null} carouselCards - Tarjetas de carousel (enriquecido)
 * @property {Object|null} contentCallPermissionReply - Datos de respuesta de permiso de llamada (enriquecido)
 * @property {string} contentCallPermissionReply.response - Respuesta de la respuesta de permiso de llamada
 * @property {boolean} contentCallPermissionReply.isPermanent - Si la respuesta de permiso de llamada es permanente
 *
 * ---
 * ESTRUCTURA DE miaSourcesResponse
 * ---
 * @property {Object|null} miaSourcesResponse - Fuentes asociadas al mensaje MIA
 * @property {Array<Object>} [miaSourcesResponse.guides] - Fuentes tipo guía
 * @property {string|number} miaSourcesResponse.guides[].id - ID único de la guía
 * @property {string} miaSourcesResponse.guides[].type - Tipo de fuente ("web", "pdf", etc.)
 * @property {string} miaSourcesResponse.guides[].title - Título de la fuente
 * @property {string} [miaSourcesResponse.guides[].url] - URL de la fuente (si aplica)
 * @property {string} miaSourcesResponse.guides[].result - Texto de resultado o resumen
 *
 * @property {Array<Object>} [miaSourcesResponse.products] - Fuentes tipo producto
 * @property {string|number} miaSourcesResponse.products[].id - ID único del producto
 * @property {string} miaSourcesResponse.products[].name - Nombre del producto
 * @property {string} miaSourcesResponse.products[].description - Descripción del producto
 * @property {Array<string>} miaSourcesResponse.products[].images - URLs de imágenes del producto
 *
 *
 * ---
 * ESTRUCTURA DE order_summary
 * ---
 * @property {Object} order_summary.saveAction - Acción de guardado
 * @property {string} order_summary.delivery_method - Método de entrega
 * @property {string} order_summary.address - Dirección
 * @property {string} order_summary.city - Ciudad
 * @property {string} order_summary.state - Estado
 * @property {string} order_summary.country - País (TODO: enviar desde backend)
 * @property {string} order_summary.zip_code - Código postal
 * @property {string} order_summary.currency - Moneda
 * @property {string} order_summary.currencySymbol - Símbolo de moneda (TODO: enviar desde backend)
 * @property {Array<Object>} order_summary.products - Productos de la orden
 * @property {string|number} order_summary.products.id - ID de producto
 * @property {string} order_summary.products.title - Título de producto
 * @property {Array} order_summary.products.variants - Variantes
 * @property {string} order_summary.products.variant_name - Nombre de variante
 * @property {number} order_summary.products.quantity - Cantidad
 * @property {number} order_summary.products.total - Total por producto
 * @property {number} order_summary.subtotal - Subtotal
 * @property {number} order_summary.total_discount - Descuento total
 * @property {number} order_summary.subtotal_with_discount - Subtotal con descuento
 * @property {number} order_summary.tax - Impuesto
 * @property {number} order_summary.shipping_value - Valor de envío
 * @property {number} order_summary.total - Total de la orden
 * @property {string|number} order_summary.web_id - ID web de la orden
 */

import { useMemo, useCallback } from "react";
import { isEmpty } from "lodash";
import { useSelector } from "react-redux";
import { formatText } from "../../../../util/textUtil";
import { checkForUrls } from "../../../../util/urlUtil";
import useSelectedConversation from "../../shared/useSelectedConversation";
import {
  isWhatsAppMsPlatform,
  toSidebarPlatformKey
} from "../../../../services/inbox/inboxMsChatPlatform";

// Keep in sync with Facebook::Messages::INSTAGRAM_SHARE_ATTACHMENT_TYPES.
const INSTAGRAM_POST_CONTENT_TYPES = new Set([
  'post',
  'share',
  'ig_post',
  'ig_reel',
  'reel',
  'story',
  'ig_story',
  'story_mention',
  'unsupported_type'
]);

const isInstagramLookasideShare = (message, normalizedType) => {
  if (normalizedType !== 'text' && normalizedType !== 'unknown') return false;
  if (message.content_text) return false;

  const mediaUrl = message.content_media_url || message.url;
  return typeof mediaUrl === 'string' && mediaUrl.includes('lookaside.fbsbx.com');
};

/**
 * Hook Data Layer para AreaChatWindowMC
 *
 * - Solo gestiona obtención y transformación de datos (Data Layer)
 * - Optimizado con useMemo/useCallback para evitar renders innecesarios
 * - Cumple con arquitectura y separación de Mercately
 *
 * @function useAreaChatWindowData
 * @param {Object} params - Parámetros de entrada
 * @param {Object} params.conversation - Objeto de conversación actual
 * @returns {AreaChatWindowDataResult} Mensajes transformados y estados asociados
 * @see AreaChatWindowMC
 * @example
 * const { messages, conversation, loading, pagination } = useAreaChatWindowData({});
 */

/**
   * @typedef {Object} AreaChatWindowDataResult
   * @description Estructura retornada por el hook Data Layer para AreaChatWindowMC.
   * @property {Array<Object>} messages - Lista de mensajes transformados y enriquecidos
   * @property {Object} conversation - Objeto de conversación actual
   * @property {boolean} loading - Estado de carga de los mensajes
   * @property {Object} pagination - Información de paginación
   */
export default function useAreaChatWindowData() {
  const conversation = useSelectedConversation();
  const { data, loading, pagination } = useSelector((state) => state.conversationMessagesReducer);

  // Si data ya contiene solo los mensajes de la conversación activa, no es necesario filtrar ni agrupar
  const conversationMessages = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data;
  }, [data]);

  // Memoized text formatter
  const formatTextMessage = useCallback((msg) => {
    if (typeof msg.content_text !== 'string') return '';
    const formatted = formatText(msg.content_text);
    return formatted || '';
  }, []);

  // Memoized location URL extractor
  const locationURL = useCallback((msg) => {
    const latitude = msg.content_location_latitude;
    const longitude = msg.content_location_longitude;
    if (isWhatsAppMsPlatform(conversation.platform) && latitude != null && longitude != null) {
      return `https://www.google.com/maps/place/${latitude},${longitude}`;
    }
    return msg.url || msg.text || msg.content_text || msg.content_media_url;
  }, [conversation.platform]);

  const getNormalizedContentType = useCallback((type, platform) => {
    if (!type) return 'text';
    if (type === 'image' || type.startsWith('image/')) return 'image';
    if (type === 'audio' || type.startsWith('audio/')) return 'audio';
    if (type === 'video' || type.startsWith('video/')) return 'video';
    if (type === 'location') return 'location';
    if (type === 'document' || type === 'file' || type.includes('application/') || type.includes('text/')) return 'document';
    if (type === 'sticker') return 'sticker';
    if (type === 'contact') return 'contact';
    if (type === 'order') return 'order';
    if (type === 'product') return 'product';
    if (type === 'carousel') return 'carousel';
    if (type === 'post') return 'post';

    const isInstagram = toSidebarPlatformKey(platform) === 'instagram';
    if (isInstagram && INSTAGRAM_POST_CONTENT_TYPES.has(type)) return 'post';

    return type;
  }, []);

  // Memoized replied message component type extractor
  const getComponentTypeReply = useCallback((attr) => {
    const type = getNormalizedContentType(attr.content_type, conversation?.platform);

    if (type === 'image') return 'RepliedImageMessageMC';

    if (type === 'audio') return 'RepliedAudioMessageMC';

    if (type === 'video') return 'RepliedVideoMessageMC';

    if (type === 'location') return 'RepliedLocationMessageMC';

    if (type === 'document') return 'RepliedDocumentMessageMC';

    if (type === 'contact') return 'RepliedContactMessageMC';

    if (type === 'sticker') return 'RepliedStickerMessageMC';

    if (type === 'order') return 'RepliedOrderWsMessageMC';

    if (type === 'product') return 'RepliedProductWsMessageMC';

    if (type === 'carousel') return 'RepliedCarouselMessageMC';

    if (type === 'post') return 'RepliedPostMessageMC';

    return 'RepliedTextMessageMC';
  }, [getNormalizedContentType, conversation?.platform]);

  // Memoized replied message extractor
  const repliedMessage = useCallback((msgReply) => {
    if (!msgReply) return null;

    const attr = msgReply?.data?.attributes || msgReply;
    const contentWsProduct = attr.content_ws_product || attr.contentWsProduct;
    const contentWsOrder = attr.content_ws_order || attr.contentWsOrder;

    return {
      id: attr.id,
      componentType: getComponentTypeReply(attr),
      isProduct: attr.content_type === 'product',
      mid: attr.mid,
      url: attr.url,
      postUrl: attr.content_media_url || attr.url,
      fileName: attr.filename,
      contentMediaMimeType: attr.content_media_mime_type,
      messageText: checkForUrls(formatTextMessage(attr)),
      contentMediaUrl: attr.content_type === 'product'
        ? (contentWsProduct?.url || attr.content_media_url)
        : attr.content_media_url,
      // Misma copia m4a que en el mensaje principal: una nota de voz citada
      // también se reproduce en Safari.
      internalUrl: attr.internal_url,
      contentWsProduct,
      // RepliedOrderWsMessageMC lee `contentWsOrder`; sin esta línea un pedido
      // citado salía vacío aunque el pedido original se viera bien.
      contentWsOrder,
      locationURL: locationURL(attr),
      contactsInformation: attr.contacts_information,
      cardHeaderType: attr.card_header_type?.toLowerCase(),
      carouselCards: attr.carousel_cards || []
    };
  }, [formatTextMessage, locationURL, getComponentTypeReply]);

  // Memoized regex extractors
  const extractFbUrl = useCallback((text) => {
    if (!text) return null;
    const urlRegex = /\b(https?:\/\/fb\.me\/\w+)\b/;
    const matches = text.match(urlRegex);
    return matches ? matches[0] : null;
  }, []);

  const extractIgUrl = useCallback((text) => {
    if (!text) return null;
    const urlRegex = /\b(https?:\/\/www\.instagram\.com\/p\/\w+)\b/;
    const matches = text.match(urlRegex);
    return matches ? matches[0] : null;
  }, []);

  // Memoized icon logic
  const shouldShowIcon = useCallback((msg) => {
    if (msg.order_note === true || msg.note === true) return false;
    if (isWhatsAppMsPlatform(conversation.platform) && msg.direction === 'outbound') return true;
    return msg.sent_by_retailer === true && (msg.date_read || msg.error_message);
  }, [conversation.platform]);

  // Memoized error logic
  // 'error' es el fallo síncrono de envío; 'failed' es el rechazo asíncrono que
  // Meta manda por webhook. Con solo 'error', un mensaje que Meta descartó se
  // mostraba como enviado hasta que además llegara un error_message.
  const showError = useCallback(
    (msg) => msg.status === 'error' || msg.status === 'failed' || !!msg.error_message,
    []
  );

  // Memoized component type logic
  const getComponentType = useCallback((msg) => {
    if (msg.mia_event || msg.mia_assignment) return 'MiaEventMessageMC';

    if (msg.mia_flag === 'buy' || msg.mia_flag === 'human_help' || msg.mia_flag === 'generated_order' || msg.mia_flag === 'order_error' || msg.mia_flag === 'payment_link') return 'MiaInternalMessageMC';

    if (msg.mia_client_data === true) return 'MiaClientDataMessageMC';

    if ((msg.content_type === 'text' && msg.note === true && msg.order_note === true)) return 'OrderNoteMessageMC';

    if ((msg.content_type === 'text' && msg.note === true)) return 'NoteMessageMC';

    if (msg.content_type === 'order') return 'OrderWsMessageMC';

    if (msg.content_type === 'product') return 'ProductWsMessageMC';

    if (msg.content_type === 'list' && !isEmpty(msg.options_list)) return 'ListMessageMC';

    if ((msg.content_type === 'quick_reply' && !isEmpty(msg.options_list)) || (msg.payload?.type === 'quick_reply' && !isEmpty(msg.payload?.list))) return 'QuickReplyMessageMC';

    const type = getNormalizedContentType(msg.content_type, conversation?.platform);

    const isInstagramConversation = toSidebarPlatformKey(conversation?.platform) === 'instagram';
    if (isInstagramConversation && isInstagramLookasideShare(msg, type)) {
      return 'PostMessageMC';
    }

    if (type === 'image') return 'ImageMessageMC';

    if (type === 'audio') return 'AudioMessageMC';

    if (type === 'video') return 'VideoMessageMC';

    if (type === 'location') return 'LocationMessageMC';

    if (type === 'document') return 'DocumentMessageMC';

    if (type === 'contact') return 'ContactMessageMC';

    if (type === 'sticker') return 'StickerMessageMC';

    if (type === 'carousel') return 'CarouselMessageMC';

    if (msg.content_type === 'call_permission_reply') return 'CallPermissionReplyMessageMC';

    if (type === 'post') return 'PostMessageMC';

    if (msg.content_type === 'interactive') return 'FlowMessageMC';

    return 'TextMessageMC';
  }, [getNormalizedContentType, conversation?.platform]);

  // Memoized content call permission reply extractor
  const getContentCallPermissionReply = useCallback((msg) => {
    if (!msg.content_call_permission_reply) return null;
    return {
      response: msg.content_call_permission_reply.response,
      isPermanent: msg.content_call_permission_reply.is_permanent
    };
  }, []);

  // Memoized messages transformation
  const messages = useMemo(() => {
    if (!conversationMessages) return [];
    if (!Array.isArray(conversationMessages)) return [];
    const mapped = conversationMessages.map((msg) => ({
      id: msg.id,
      accountUID: msg.account_uid,
      assignmentData: msg.assignment_data,
      componentType: getComponentType(msg),
      messageText: checkForUrls(formatTextMessage(msg)),
      fbUrl: ENV.INTEGRATION === '2' ? extractFbUrl(formatTextMessage(msg)) : null,
      igUrl: ENV.INTEGRATION === '2' ? extractIgUrl(formatTextMessage(msg)) : null,
      postUrl: msg.content_media_url || msg.url,
      contentType: msg.content_type,
      contentMediaType: msg.content_media_type || msg.content_type,
      contentMediaUrl: msg.content_type === 'product'
        ? (msg.content_ws_product?.url || msg.content_media_url)
        : msg.content_media_url,
      // Copia m4a de una nota de voz: se entrega en Ogg/Opus, que Safari no
      // decodifica. AudioMessageMC la prefiere sobre contentMediaUrl.
      internalUrl: msg.internal_url,
      direction: msg.direction,
      senderFullName: msg.sender_full_name,
      inboundSenderFullName: msg.inbound_sender_full_name,
      isFromGroup: !!msg.from_group,
      contentMediaCaption: checkForUrls(msg.content_media_caption),
      createdTime: msg.created_time,
      customerId: msg.customer_id,
      status: msg.status,
      fileName: msg.filename,
      contentMediaMimeType: msg.content_media_mime_type,
      contentLocationLatitude: msg.content_location_latitude,
      contentLocationLongitude: msg.content_location_longitude,
      locationURL: locationURL(msg),
      orderSummary: msg.order_summary,
      isNote: (msg.content_type === 'text' && !!msg.note && msg.order_note !== true),
      isOrderNote: (msg.content_type === 'text' && !!msg.note && !!msg.order_note),
      showError: showError(msg),
      errorMessage: msg.error_message,
      errorDetails: msg.error_details || null,
      contactsInformation: msg.contacts_information,
      optionsList: msg.options_list || [],
      contentWsOrder: msg.content_ws_order,
      contentWsProduct: msg.content_ws_product,
      eventData: msg.event_data,
      isBuyIntention: msg.mia_flag === 'buy',
      isGeneratedOrder: msg.mia_flag === 'generated_order',
      isHumanHelp: msg.mia_flag === 'human_help',
      isOrderError: msg.mia_flag === 'order_error',
      isPaymentLink: msg.mia_flag === 'payment_link',
      isMiaAssignment: !!msg.mia_assignment,
      isMiaClientData: !!msg.mia_client_data,
      isMiaEvent: !!msg.mia_event,
      isSentByMia: !!msg.sent_by_mia,
      isShowIcon: shouldShowIcon(msg),
      mid: msg.mid,
      repliedMessage: repliedMessage(msg.replied_message),
      identifier: msg.identifier,
      miaSourcesResponse: msg.mia_sources_response,
      reaction: msg.reaction,
      canReply: msg.can_reply,
      cardHeaderType: msg.card_header_type?.toLowerCase(),
      carouselCards: msg.carousel_cards || [],
      contentCallPermissionReply: getContentCallPermissionReply(msg),
      contentFlow: msg.content_flow || null
    }));
    return mapped;
  }, [
    formatTextMessage,
    extractFbUrl,
    extractIgUrl,
    shouldShowIcon,
    getComponentType,
    showError,
    locationURL,
    conversationMessages,
    repliedMessage,
    getContentCallPermissionReply
  ]);

  // Memoized return object
  return useMemo(() => ({
    messages,
    conversation,
    loading,
    pagination
  }), [
    messages,
    conversation,
    loading,
    pagination
  ]);
}
